// Plan Summary Hook - All Business Logic (WITH PDF DOWNLOAD)
// Location: src/hooks/usePlanSummary.ts

import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { pdf } from '@react-pdf/renderer';
import { useAuthStore } from '../stores/authStore';
import { supabase } from '../lib/supabase';
import { PlanSummaryDocument } from '../components/pdf/PlanSummaryDocument';

interface PlanSummary {
  id: string;
  plan_id: string;
  start_weight: number;
  end_weight: number;
  weight_change: number;
  goal_weight: number;
  goal_achieved: boolean;
  days_completed: number;
  total_days: number;
  completion_percentage: number;
  meals_logged: number;
  total_meals: number;
  meal_adherence_percentage: number;
  avg_calories: number;
  avg_protein: number;
  avg_carbs: number;
  avg_fat: number;
  weekly_stats: any[];
  best_days: any[];
  worst_days: any[];
  insights: any[];
  achievements_unlocked: any[];
  generated_at: string;
  generated_by: string;
}

interface NutritionPlan {
  title: string;
  goal: string;
  plan_start_date: string;
  duration_days: number;
}

interface UsePlanSummaryReturn {
  // State
  summary: PlanSummary | null;
  plan: NutritionPlan | null;
  isLoading: boolean;
  isGenerating: boolean;
  isDownloadingPDF: boolean;
  error: string | null;
  
  // Actions
  loadSummary: () => Promise<void>;
  generateSummary: () => Promise<void>;
  downloadPDF: () => Promise<void>;
  shareResults: () => void;
  
  // Computed
  planEndDate: Date | null;
  formatDate: (date: Date) => string;
  formatTime: (date: Date) => string;
}

export function usePlanSummary(): UsePlanSummaryReturn {
  const { t, i18n } = useTranslation();
  const { planId } = useParams<{ planId: string }>();
//   const navigate = useNavigate();
  const { user } = useAuthStore();

  const [summary, setSummary] = useState<PlanSummary | null>(null);
  const [plan, setPlan] = useState<NutritionPlan | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Computed: Plan end date
  const planEndDate = plan ? (() => {
    const endDate = new Date(plan.plan_start_date);
    endDate.setDate(endDate.getDate() + plan.duration_days);
    return endDate;
  })() : null;

  // Format date based on user's locale
  const formatDate = useCallback((date: Date) => {
    return date.toLocaleDateString(i18n.language, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  }, [i18n.language]);

  // Format time based on user's locale
  const formatTime = useCallback((date: Date) => {
    return date.toLocaleTimeString(i18n.language, { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  }, [i18n.language]);

  // Load summary from database
  const loadSummary = useCallback(async () => {
    if (!planId) return;

    try {
      setIsLoading(true);
      setError(null);

      // Fetch summary
      const { data: summaryData, error: summaryError } = await supabase
        .from('plan_summaries')
        .select('*')
        .eq('plan_id', planId)
        .single();

      if (summaryError && summaryError.code !== 'PGRST116') {
        throw summaryError;
      }

      if (!summaryData) {
        // No summary exists, generate one
        await generateSummary();
        return;
      }

      setSummary(summaryData);

      // Fetch plan details
      const { data: planData, error: planError } = await supabase
        .from('nutrition_plans')
        .select('title, goal, plan_start_date, duration_days')
        .eq('id', planId)
        .single();

      if (planError) throw planError;
      setPlan(planData);

    } catch (err: any) {
      console.error('[usePlanSummary] Failed to load summary:', err);
      setError(err.message || t('planSummary.error.title'));
    } finally {
      setIsLoading(false);
    }
  }, [planId, t]);

  // Generate new summary
  const generateSummary = useCallback(async () => {
    if (!user?.id || !planId) return;

    try {
      setIsGenerating(true);
      setError(null);

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || process.env.NEXT_PUBLIC_SUPABASE_URL;
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(`${supabaseUrl}/functions/v1/generate-plan-summary`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          plan_id: planId,
          user_id: user.id,
          triggered_by: 'user_trigger'
        })
      });

      if (!response.ok) {
        throw new Error(t('planSummary.alerts.summaryGenerationFailed'));
      }

      const result = await response.json();

      if (result.success) {
        // Reload summary
        await loadSummary();
      } else {
        throw new Error(result.error || t('planSummary.alerts.summaryGenerationFailed'));
      }

    } catch (err: any) {
      console.error('[usePlanSummary] Failed to generate summary:', err);
      setError(err.message || t('planSummary.alerts.summaryGenerationFailed'));
      toast.error(err.message || t('planSummary.alerts.summaryGenerationFailed'));
    } finally {
      setIsGenerating(false);
    }
  }, [user?.id, planId, t, loadSummary]);

  // Download PDF
  const downloadPDF = useCallback(async () => {
    if (!summary || !plan || !user) {
      toast.error(t('planSummary.alerts.pdfGenerationFailed'));
      return;
    }

    try {
      setIsDownloadingPDF(true);
      toast.loading(t('planSummary.alerts.generatingPDF'));

      // Create PDF document
      const doc = (
        <PlanSummaryDocument
          summary={summary}
          plan={plan}
          userProfile={user}
          language={i18n.language as 'de' | 'en'}
        />
      );

      // Generate PDF blob
      const blob = await pdf(doc).toBlob();

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with date
      const planTitle = plan.title || 'Plan';
      const dateStr = new Date().toISOString().split('T')[0];
      link.download = `NutriBot_Summary_${planTitle}_${dateStr}.pdf`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup
      URL.revokeObjectURL(url);

      toast.dismiss();
      toast.success(t('planSummary.alerts.pdfDownloaded'));

    } catch (err: any) {
      console.error('[usePlanSummary] Failed to download PDF:', err);
      toast.dismiss();
      toast.error(t('planSummary.alerts.pdfGenerationFailed'));
    } finally {
      setIsDownloadingPDF(false);
    }
  }, [summary, plan, user, i18n.language, t]);

  // Share results
  const shareResults = useCallback(() => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    toast.success(t('planSummary.alerts.linkCopied'));
  }, [t]);

  // Load summary on mount
  useEffect(() => {
    if (planId) {
      loadSummary();
    }
  }, [planId, loadSummary]);

  return {
    // State
    summary,
    plan,
    isLoading,
    isGenerating,
    isDownloadingPDF,
    error,
    
    // Actions
    loadSummary,
    generateSummary,
    downloadPDF,
    shareResults,
    
    // Computed
    planEndDate,
    formatDate,
    formatTime,
  };
}